import { ReactEditor, RenderElementProps } from 'slate-react';
import { TranscriptSpeaker } from '@components/editors/transcript-editor/body/transcript-speaker.tsx';
import {
  DocumentTranscriptParagraphV1,
  ScriptixDescendant
} from '@utils/types/editors/transcripts/transcript.ts';
import { useEffect, useRef } from 'react';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { changeSpeaker } from '@utils/editors/change-speaker.ts';
import { currentParagraphIsSeeking } from '@utils/editors/current-paragraph-is-seeking.ts';
import { useTranscript } from '@utils/providers/transcript-context.tsx';
import { useEditorContext } from '@utils/providers/editor-context.tsx';

export const ParagraphElement = ({
  attributes,
  children,
  element,
  onWordOutOfView,
  onWordInView,
  editor,
  index
}: RenderElementProps & {
  onWordOutOfView: (
    ref: HTMLSpanElement | null,
    direction: 'up' | 'down'
  ) => void;
  onWordInView: () => void;
  editor: ReactEditor;
  index: number;
}) => {
  const { currentTime, linkedPlayback } = useAudio();
  const { addSpeaker, replaceSpeakers } = useTranscript();
  const paragraphRef = useRef<HTMLDivElement | null>(null);
  const { replaceSpeakersInEditor } = useEditorContext();

  const isCurrentWord = currentParagraphIsSeeking(
    element as DocumentTranscriptParagraphV1,
    currentTime
  );
  const { type, id, speaker, start } = element as ScriptixDescendant;

  useEffect(() => {
    if (!linkedPlayback) return;

    const observer = new IntersectionObserver(
      entries => {
        try {
          entries.forEach(entry => {
            if (isCurrentWord) {
              const direction =
                entry.boundingClientRect.top < 0 ? 'up' : 'down';

              if (!entry.isIntersecting && entry.intersectionRatio === 0) {
                onWordOutOfView(paragraphRef.current, direction);
              } else if (entry.isIntersecting && entry.intersectionRatio > 0) {
                onWordInView();
              }
            }
          });
        } catch (error) {
          // Silently handle intersection observer errors
          // Error is caught and ignored intentionally
        }
      },
      { root: null, threshold: [0, 0.1, 0.5, 1] }
    );

    if (paragraphRef.current) observer.observe(paragraphRef.current);
    return () => {
      if (paragraphRef.current) observer.unobserve(paragraphRef.current);
    };
  }, [isCurrentWord, onWordOutOfView, onWordInView, linkedPlayback]);

  const handleSetSpeaker = (value: string) => {
    try {
      addSpeaker(value, element as ScriptixDescendant);
      changeSpeaker(value, element as ScriptixDescendant, editor);
    } catch (error) {
      // Silently handle Slate DOM resolution errors
      // Error is caught and ignored intentionally
    }
  };

  const handleReplaceSpeakers = (value: string) => {
    try {
      if (!value || !speaker) return;
      replaceSpeakers(speaker, value); // state
      replaceSpeakersInEditor(speaker, value); // UI
    } catch (error) {
      // Silently handle Slate DOM resolution errors
      // Error is caught and ignored intentionally
    }
  };

  const handleInteraction = () => {
    // Broadcast that this row is now active
    window.dispatchEvent(
      new CustomEvent('transcript:activeRow', { detail: { index } })
    );
  };

  // Comments gutter (#2244) anchors each card to a paragraph by
  // `data-comment-anchor`. The transcript comment API stores the
  // anchor as a stringified utterance start timestamp — match that
  // exactly so lookup is a simple `querySelector`.
  return (
    <div ref={paragraphRef} data-comment-anchor={String(start ?? 0)}>
      {type === 'speaker' ? (
        <div
          className="flex items-start w-full"
          key={id}
          {...attributes}
          contentEditable={false}
          onMouseDown={handleInteraction}
          onClick={handleInteraction}
        >
          <TranscriptSpeaker
            speaker={speaker || ''}
            start={start || 0}
            setSpeaker={handleSetSpeaker}
            index={index}
            replaceSpeakers={handleReplaceSpeakers}
          />
        </div>
      ) : (
        <div
          {...attributes}
          className="transition-colors duration-150"
          onMouseDown={handleInteraction}
          onClick={handleInteraction}
          onFocus={handleInteraction}
        >
          {children}
        </div>
      )}
    </div>
  );
};
